import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import toast from "react-hot-toast";
import {
  CreateBillingAddress,
  CreateShippingAddress,
  getCities,
  getcountry,
  getStates,
} from "services/apiService";

const AddressForm = ({ type, userId, onAutoComplete }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const [formData, setFormData] = useState(
    type === "billing"
      ? {
          billing_name: "",
          billing_email: "",
          billing_phone_no: "",
          billing_address_line_one: "",
          billing_address_line_two: "",
          billing_country: "",
          billing_state: "",
          billing_city: "",
          billing_pincode: "",
        }
      : {
          shipping_name: "",
          shipping_email: "",
          shipping_phone_no: "",
          shipping_address_line_one: "",
          shipping_address_line_two: "",
          shipping_country: "",
          shipping_state: "",
          shipping_city: "",
          shipping_pincode: "",
        }
  );

  const [errors, setErrors] = useState({});

  const validateField = (field: string, value: string) => {
    let error = "";
    if (field === `${type}_name` && value.trim() === "") {
      error = "Name is required";
    } else if (field === `${type}_email`) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) {
        error = "Invalid email address";
      }
    } else if (field === `${type}_phone_no`) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(value)) {
        error = "Phone number must be 10 digits";
      }
    } else if (field === `${type}_address_line_one` && value.trim() === "") {
      error = "Address Line 1 is required";
    } else if (field === `${type}_pincode`) {
      const pincodeRegex = /^[1-9][0-9]{5}$/;
      if (!pincodeRegex.test(value)) {
        error = "Invalid pincode";
      }
    }
    return error;
  };

  const handleChange = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors({ ...errors, [field]: error });

    const numericFields = [`${type}_phone_no`, `${type}_pincode`];
    const parsedValue = numericFields.includes(field) ? Number(value) : value;

    setFormData({
      ...formData,
      [field]: parsedValue,
    });
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await getcountry();
      if (response.status_code === 200) {
        setCountries(response.data);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchStates = async (countryId: number) => {
    try {
      const response = await getStates(countryId);
      if (response.status_code === 200) {
        setStates(response.data);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchCities = async (stateId: number) => {
    try {
      const response = await getCities(stateId);
      if (response.status_code === 200) {
        setCities(response.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        user_id: userId,
        ...formData,
        [`${type}_set_default`]: 1,
        [`${type}_is_active`]: 1,
      };

      const response =
        type === "billing"
          ? await CreateBillingAddress(payload)
          : await CreateShippingAddress(payload);

      if (response.status_code === 200) {
        toast.success(
          `${
            type.charAt(0).toUpperCase() + type.slice(1)
          } Address added successfully`
        );
        onAutoComplete();
      }
    } catch (error) {
      console.log("Error:", error);
      toast.error("Failed to add address");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        maxWidth: "750px",
        margin: "0 auto",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        bgcolor: "background.paper",
      }}
    >
      <TextField
        label="Name"
        value={formData[`${type}_name`]}
        onChange={(e) => handleChange(`${type}_name`, e.target.value)}
        fullWidth
        required
        error={!!errors[`${type}_name`]}
        helperText={errors[`${type}_name`]}
      />
      <TextField
        label="Email"
        type="email"
        value={formData[`${type}_email`]}
        onChange={(e) => handleChange(`${type}_email`, e.target.value)}
        fullWidth
        required
        error={!!errors[`${type}_email`]}
        helperText={errors[`${type}_email`]}
      />
      <TextField
        label="Phone Number"
        type="tel"
        value={formData[`${type}_phone_no`]}
        onChange={(e) => handleChange(`${type}_phone_no`, e.target.value)}
        fullWidth
        required
        error={!!errors[`${type}_phone_no`]}
        helperText={errors[`${type}_phone_no`]}
      />
      <TextField
        label="Address Line 1"
        value={formData[`${type}_address_line_one`]}
        onChange={(e) =>
          handleChange(`${type}_address_line_one`, e.target.value)
        }
        fullWidth
        required
        error={!!errors[`${type}_address_line_one`]}
        helperText={errors[`${type}_address_line_one`]}
      />
      <TextField
        label="Address Line 2"
        value={formData[`${type}_address_line_two`]}
        onChange={(e) =>
          handleChange(`${type}_address_line_two`, e.target.value)
        }
        fullWidth
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Town/Locality"
          value={formData[`${type}_city`]}
          onChange={(e) => handleChange(`${type}_city`, e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Pincode"
          value={formData[`${type}_pincode`]}
          onChange={(e) => handleChange(`${type}_pincode`, e.target.value)}
          fullWidth
          required
          error={!!errors[`${type}_pincode`]}
          helperText={errors[`${type}_pincode`]}
        />
      </Box>
      <Autocomplete
        id="country-select-demo"
        options={countries}
        getOptionLabel={(option: any) => option.country_name}
        value={selectedCountry}
        onChange={(event, newValue) => {
          setSelectedCountry(newValue);
          handleChange(`${type}_country`, newValue ? newValue.country_id : "");
          setStates([]);
          setCities([]);
          if (newValue) fetchStates(newValue.country_id);
        }}
        renderInput={(params) => (
          <TextField {...params} label="Country" fullWidth required />
        )}
      />

      <Autocomplete
        id="state-select-demo"
        options={states}
        disabled={!selectedCountry}
        getOptionLabel={(option: any) => option.state_name}
        value={selectedState}
        onChange={(event, newValue) => {
          setSelectedState(newValue);
          handleChange(`${type}_state`, newValue ? newValue.state_id : "");
          setCities([]);
          if (newValue) fetchCities(newValue.state_id);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="State"
            fullWidth
            required
            disabled={!selectedCountry}
          />
        )}
      />

      <Autocomplete
        id="city-select-demo"
        options={cities}
        disabled={!selectedState}
        getOptionLabel={(option: any) => option?.city_name}
        value={selectedCity}
        onChange={(event, newValue) => {
          setSelectedCity(newValue);
          handleChange(`${type}_city`, newValue ? newValue.city_id : "");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City"
            fullWidth
            required
            disabled={!selectedState}
          />
        )}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        fullWidth
        disabled={Object.values(formData).some(
          (value) =>
            !value ||
            errors[`${type}_name`] ||
            errors[`${type}_email`] ||
            errors[`${type}_phone_no`] ||
            errors[`${type}_address_line_one`] ||
            errors[`${type}_pincode`]
        )}
      >
        ADD {type?.charAt(0).toUpperCase() + type?.slice(1)} ADDRESS
      </Button>
    </Box>
  );
};

export default AddressForm;
